import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Cookies auth: send credentials on every request; CSRF token added below.
export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

const _uuid = () =>
  (crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

// In-memory CSRF token cache — used as fallback when cookie is unavailable.
let _csrfTokenCache = null;

async function fetchCsrfToken() {
  try {
    const r = await axios.get(`${API_BASE}/auth/csrf`, { withCredentials: true });
    _csrfTokenCache = r.data?.csrf_token || null;
    return _csrfTokenCache;
  } catch {
    return null;
  }
}

function getCsrfToken() {
  return getCookie("algoforge_csrf") || _csrfTokenCache || null;
}

const UNSAFE = new Set(["post", "put", "patch", "delete"]);

api.interceptors.request.use(async (config) => {
  // Idempotency for paper orders (per-click UUID, keeps replay-safe semantics).
  const url = config.url || "";
  if (
    config.method?.toLowerCase() === "post" &&
    (url.startsWith("/paper/order") || url.includes("/paper/order/multi-leg")) &&
    !config.headers["Idempotency-Key"]
  ) {
    config.headers["Idempotency-Key"] = _uuid();
  }

  // Double-submit CSRF: echo the algoforge_csrf cookie on every state-changing
  // request. If cookie is absent (session pre-dates CSRF or cookie expired),
  // fetch a fresh token from /auth/csrf and cache it.
  const method = (config.method || "get").toLowerCase();
  if (UNSAFE.has(method) && !config._csrfRetry) {
    let csrf = getCsrfToken();
    if (!csrf) {
      csrf = await fetchCsrfToken();
    }
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }

  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Auto-recover from CSRF mismatch: fetch a fresh token then retry once.
    if (
      err?.response?.status === 403 &&
      err?.response?.data?.detail === "Missing CSRF token" &&
      !err.config._csrfRetry
    ) {
      const token = await fetchCsrfToken();
      if (token) {
        err.config._csrfRetry = true;
        err.config.headers["X-CSRF-Token"] = token;
        return api.request(err.config);
      }
    }
    if (err?.response?.status === 401) {
      const PUBLIC_PATHS = ["/login", "/register", "/", "/about", "/faq", "/blog", "/learn", "/pricing"];
      const path = window.location.pathname;
      const isPublic = PUBLIC_PATHS.some((p) => path === p || path.startsWith("/blog/") || path.startsWith("/learn/") || path.startsWith("/strategy-guide/"));
      // Also skip redirect for the bootstrap /auth/me check — it always 401s for visitors
      const isAuthMe = err.config?.url === "/auth/me";
      if (!isPublic && !isAuthMe) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export default api;
